import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { tap } from "rxjs/operators";
import { HttpManager } from "@service/system/httpManager.service";
import { ServiceResponse, ServerErrorResponse } from "@data/response";

/**
 * A temporary service that transforms http calls for the http manager using the standardized response format.
 *
 * @author    		John Ying <john.ying@childrenshospitals.org>
 * @created   		7/31/2023
 */
@Injectable()
export class ResponseHandler {
	constructor(private httpManager: HttpManager) {}

	/**
	 * Makes an http GET call to the HUB database and returns an Observable of the given type.
	 *
	 * @param url       - the full URL address
	 * @param params    - the key-value query parameters
	 *
	 * @returns the http GET response
	 */
	httpGet<T>(url: string, params?: Record<string, unknown>): Observable<ServiceResponse<T>> {
		return this.httpManager.httpGet<ServiceResponse<T>>(url, params).pipe(tap(this.catchErrorResponse));
	}

	/**
	 * Makes an http POST call to the HUB database and returns an Observable of the given type.
	 *
	 * @param url       - the full URL address
	 * @param body      - the POST body
	 * @param params    - the key-value query parameters
	 *
	 * @returns the http POST response
	 */
	httpPost<T>(url: string, body: any | null, params?: Record<string, unknown>): Observable<ServiceResponse<T>> {
		return this.httpManager.httpPost<ServiceResponse<T>>(url, body, params).pipe(tap(this.catchErrorResponse));
	}

	/**
	 * Makes an http DELETE call to the HUB database and returns an Observable of the given type.
	 *
	 * @param url       - the full URL address
	 * @param params    - the key-value query parameters
	 *
	 * @returns the http DELETE response
	 */

	httpDelete<T>(url: string, params?: Record<string, unknown>): Observable<ServiceResponse<T>> {
		return this.httpManager.httpDelete<ServiceResponse<T>>(url, params).pipe(tap(this.catchErrorResponse));
	}

	private catchErrorResponse<T>(response: ServiceResponse<T>) {
		if (response.status == 0) {
			const errorResponse = response.payload as unknown as ServerErrorResponse;
			const errorMessage = `${errorResponse.Message}`;
			console.error(response);
			throw (errorMessage);
		}
	}
}
